import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";
import { useState } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const ShowerTypeCard = ({ title, description, image }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-[#F9F9F7] overflow-hidden group"
  >
    <div className="h-64 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
    </div>
    <div className="p-8">
      <h3 className="font-heading text-2xl font-medium text-[#1A202C] mb-3">{title}</h3>
      <p className="font-body text-[#4B5563]">{description}</p>
    </div>
  </motion.div>
);

const CustomShowerGlassPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="pt-20">
      <SEO
        title="Custom Shower Glass"
        description="Custom frameless, semi-frameless, sliding, and hinged shower enclosures in coastal NC. Brunswick Glass delivers precise measurements and professional installation. Free quotes available."
        keywords="frameless shower, custom shower glass, semi-frameless shower, sliding shower door, shower enclosure NC, Brunswick County shower installation"
        ogImage="https://images.unsplash.com/photo-1630699144565-a42d8db6da02?w=1200&q=80"
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#F9F9F7]">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1630699144565-a42d8db6da02?w=1920&q=80"
            alt="Custom shower glass"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="section-container relative z-10 w-full py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-[#0F4C81] font-body mb-6 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4"
            >
              Residential Services
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6"
            >
              Custom Shower Glass
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-xl leading-relaxed text-[#4B5563] mb-8"
            >
              Custom glass showers are our specialty. We design, measure, and install shower enclosures that are tailored to your space, style, and daily use. Every shower is precisely measured to ensure clean lines, proper water containment, and long-term durability.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] hover:bg-[#0A355C] text-white rounded-full px-8 h-14 font-body font-semibold">
                    Get a Free Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] p-8">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onClose={() => setQuoteOpen(false)} />
                </DialogContent>
              </Dialog>
              <a href="tel:910-457-4757">
                <Button variant="outline" className="border-2 border-[#1A202C] rounded-full px-8 h-14 font-body font-semibold">
                  <Phone className="w-5 h-5 mr-2" /> 910-457-4757
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Shower Types */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">
              Shower Enclosure Types
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl">
              We offer a variety of shower enclosure styles to match your bathroom design and functional needs.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <ShowerTypeCard
              title="Frameless Showers"
              description="Sleek, modern enclosures with minimal hardware that showcase tile and natural light. Our frameless showers create an open, luxurious feel in any bathroom."
              image="https://images.unsplash.com/photo-1630699144565-a42d8db6da02?w=800&q=80"
            />
            <ShowerTypeCard
              title="Semi-Frameless Showers"
              description="A balance of structure and style, offering added support with a refined look. Perfect for those who want durability without sacrificing elegance."
              image="https://images.unsplash.com/photo-1706670351471-14d52ffa2e67?w=800&q=80"
            />
            <ShowerTypeCard
              title="Sliding Glass Showers"
              description="Ideal for tighter spaces, combining smooth operation with a clean appearance. Our sliding doors glide effortlessly on premium tracks."
              image="https://images.unsplash.com/photo-1718894070114-6de0e98449a2?w=800&q=80"
            />
            <ShowerTypeCard
              title="Hinged & Fixed Panel Showers"
              description="Custom configurations designed for unique layouts and larger openings. We create solutions that fit your exact specifications."
              image="https://images.unsplash.com/photo-1566446896748-6075a87760c1?w=800&q=80"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl font-medium text-white mb-4">Ready to Transform Your Bathroom?</h2>
          <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote. We'll help you choose the perfect shower enclosure for your space.
          </p>
          <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-[#0F4C81] hover:bg-white/90 rounded-full px-8 h-14 font-body font-semibold">
                Get Your Free Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-8">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle>
              </DialogHeader>
              <QuoteForm onClose={() => setQuoteOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default CustomShowerGlassPage;
